import { useApi } from '@hooks/useApi';
import { AcquirerUser, ApiError } from '@root/globalTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '@utils/consts';
import { CreateAcquirerUserData } from '@services/types';

export type UserResponse = AcquirerUser | ApiError;

export function isFailure(data: UserResponse): data is ApiError {
  return (data as ApiError)?.message !== undefined;
}

type CreateUserParams = {
  acquirerId: string;
  callback: (data: AcquirerUser) => void;
};

export function useCreateAcquirerUser({ acquirerId, callback }: CreateUserParams) {
  const { query } = useApi();
  const queryClient = useQueryClient();
  const [apiError, setApiError] = useState<ApiError | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (userData: CreateAcquirerUserData): Promise<UserResponse> => {
      return query({
        path: `v1/staff/acquirers/${acquirerId}/users`,
        data: userData,
        opt: {
          method: 'POST',
        },
      });
    },
    onSettled: (data) => {
      if (!data) return;
      if (isFailure(data)) {
        setApiError(data);
      } else {
        callback(data);
      }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [...queryKeys.acquirerUsers, acquirerId] });
    },
  });

  return {
    mutate,
    isPending,
    error: apiError,
  };
}
